import React, { useState } from 'react';

export default function ShareButtons({ url, title, summary, image, compact = false }) {
  const [copied, setCopied] = useState(false);

  // Fallback to window.location.href if url not passed
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = title || 'Heidi Svelmøe - Blog';
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedSummary = encodeURIComponent(summary || '');
  const encodedImage = image ? encodeURIComponent(image) : '';

  const handleCopy = async (e) => {
    e.preventDefault();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Kopiering mislykkedes:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: summary || shareTitle,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Deling mislykkedes:', err);
        }
      }
    }
  };

  const shareLinks = [
    {
      name: 'Facebook',
      icon: 'fa-brands fa-facebook-f',
      bgClass: 'hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      ariaLabel: 'Del på Facebook',
    },
    {
      name: 'LinkedIn',
      icon: 'fa-brands fa-linkedin-in',
      bgClass: 'hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      ariaLabel: 'Del på LinkedIn',
    },
    {
      name: 'E-mail',
      icon: 'fa-regular fa-envelope',
      bgClass: 'hover:bg-clay-600 hover:text-white hover:border-clay-600',
      href: `mailto:?subject=${encodedTitle}&body=${encodedSummary}%0A%0ALæs mere her: ${encodedUrl}`,
      ariaLabel: 'Del via e-mail',
    },
  ];

  if (compact) {
    return (
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <span className="text-xs text-foreground/60 mr-1 hidden sm:inline">Del:</span>
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-sand-100 text-foreground/70 hover:bg-clay-600 hover:text-white transition-all text-xs"
            title="Del på mobil"
            aria-label="Del"
          >
            <i className="fa-solid fa-share-nodes" aria-hidden="true" />
          </button>
        )}
        <a
          href={shareLinks[0].href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-7 h-7 rounded-full bg-sand-100 text-foreground/70 hover:bg-[#1877F2] hover:text-white transition-all text-xs"
          title="Del på Facebook"
          aria-label="Del på Facebook"
        >
          <i className="fa-brands fa-facebook-f" aria-hidden="true" />
        </a>
        <a
          href={shareLinks[1].href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center w-7 h-7 rounded-full bg-sand-100 text-foreground/70 hover:bg-[#0A66C2] hover:text-white transition-all text-xs"
          title="Del på LinkedIn"
          aria-label="Del på LinkedIn"
        >
          <i className="fa-brands fa-linkedin-in" aria-hidden="true" />
        </a>
        <button
          onClick={handleCopy}
          className={`flex items-center justify-center w-7 h-7 rounded-full transition-all text-xs ${
            copied
              ? 'bg-green-600 text-white'
              : 'bg-sand-100 text-foreground/70 hover:bg-clay-600 hover:text-white'
          }`}
          title={copied ? 'Kopieret!' : 'Kopier link'}
          aria-label={copied ? 'Link kopieret' : 'Kopier link'}
        >
          <i className={`fa-solid ${copied ? 'fa-check' : 'fa-link'}`} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-5 px-6 rounded-2xl bg-white border border-sand-200 shadow-sm">
      <div className="flex items-center gap-2.5 text-foreground/80 font-medium text-sm">
        <i className="fa-solid fa-share-nodes text-clay-600 text-base" aria-hidden="true" />
        <span>Del dette indlæg:</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-sand-300 text-foreground/80 hover:bg-clay-600 hover:text-white hover:border-clay-600 transition-all text-xs font-medium shadow-xs"
            aria-label="Del via telefonens apps"
          >
            <i className="fa-solid fa-arrow-up-from-bracket" aria-hidden="true" />
            <span className="hidden sm:inline">Del</span>
          </button>
        )}

        {shareLinks.map((link) => (
          <a
            key={link.name}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center w-9 h-9 rounded-full border border-sand-300 text-foreground/70 bg-sand-50 ${link.bgClass} transition-all text-sm shadow-xs`}
            title={link.name}
            aria-label={link.ariaLabel}
          >
            <i className={link.icon} aria-hidden="true" />
          </a>
        ))}

        <button
          onClick={handleCopy}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-xs font-medium transition-all shadow-xs ${
            copied
              ? 'bg-green-600 text-white border-green-600'
              : 'border-sand-300 text-foreground/80 bg-sand-50 hover:bg-clay-600 hover:text-white hover:border-clay-600'
          }`}
          aria-label={copied ? 'Link kopieret' : 'Kopier link'}
        >
          <i className={`fa-solid ${copied ? 'fa-check' : 'fa-link'}`} aria-hidden="true" />
          <span>{copied ? 'Kopieret!' : 'Kopiér link'}</span>
        </button>
      </div>
    </div>
  );
}
